import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Activity, BarChart3, Copy, Loader2, Plus, Radar, Trash2, Trophy } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'
import LoadingSpinner from '@/components/Common/LoadingSpinner'

const DEFAULT_SCANNER = {
  name: 'Nuevo escáner',
  is_active: true,
  config: {
    assets: [{ symbol: '', source: 'bingx', twelve_type: 'all' }],
    timeframes: ['15m', '1h'],
    indicators: [],
    send_alert: true,
    ema50_filter: true,
  },
}

export default function AlgorithmicScannerPage() {
  const navigate = useNavigate()
  const [scanners, setScanners] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [diagnosingId, setDiagnosingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadScanners()
  }, [])

  const loadScanners = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await algorithmicScannerService.listScanners()
      setScanners(data)
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la lista de scanners')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const existing = new Set(scanners.map((s) => s.name))
      let name = DEFAULT_SCANNER.name
      let n = 2
      while (existing.has(name)) {
        name = `${DEFAULT_SCANNER.name} ${n}`
        n += 1
      }
      const { data } = await algorithmicScannerService.createScanner({ ...DEFAULT_SCANNER, name })
      navigate(`/scanner-algorithmic/${data.id}`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear el scanner')
    } finally {
      setCreating(false)
    }
  }

  const handleDuplicate = async (scanner) => {
    setCreating(true)
    setError(null)
    try {
      // La lista no incluye la config completa; hay que cargarla antes de duplicar
      const { data: full } = await algorithmicScannerService.getScanner(scanner.id)
      const payload = {
        name: `${scanner.name} (copia)`,
        is_active: false,
        config: full.config,
      }
      const { data } = await algorithmicScannerService.createScanner(payload)
      navigate(`/scanner-algorithmic/${data.id}`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al duplicar el scanner')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este scanner?')) return
    setDeletingId(id)
    setError(null)
    try {
      await algorithmicScannerService.deleteScanner(id)
      setScanners((prev) => prev.filter((s) => s.id !== id))
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al eliminar el scanner')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDiagnose = async (id) => {
    setDiagnosingId(id)
    setError(null)
    try {
      await algorithmicScannerService.diagnoseScan(id)
      navigate(`/scanner-algorithmic/${id}?tab=diagnosis`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al lanzar el diagnóstico')
    } finally {
      setDiagnosingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-600/10 text-blue-600 dark:text-blue-400">
            <Radar size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Escáner Algorítmico</h1>
            <p className="text-xs text-slate-500 dark:text-gray-400">Crea y gestiona scanners independientes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/scanner-algorithmic/rankings"
            className="btn-secondary flex items-center gap-2"
          >
            <Trophy size={16} />
            Rankings MC
          </Link>
          <button
            onClick={() => navigate('/scanner-algorithmic/dashboard')}
            className="btn-secondary flex items-center gap-2"
          >
            <BarChart3 size={16} />
            Dashboard global
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="btn-primary flex items-center gap-2"
          >
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Nuevo scanner
          </button>
        </div>
      </div>

      {error && (
        <div className="card border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {scanners.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-slate-600 dark:text-gray-300 mb-4">Aún no tienes ningún scanner.</p>
          <button onClick={handleCreate} className="btn-primary">Crear primer scanner</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {scanners.map((scanner) => (
            <div
              key={scanner.id}
              onClick={() => navigate(`/scanner-algorithmic/${scanner.id}`)}
              className="card cursor-pointer hover:border-blue-500/30 transition-colors group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {scanner.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                    {scanner.assets_count} activos · {scanner.timeframes_count} timeframes · {scanner.indicators_count} indicadores
                  </p>
                </div>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${scanner.is_active ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-gray-800 text-slate-600 dark:text-gray-400'}`}>
                  {scanner.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200 dark:border-gray-800">
                <button
                  onClick={(e) => { e.stopPropagation(); handleDiagnose(scanner.id) }}
                  disabled={diagnosingId === scanner.id}
                  className="btn-ghost text-xs flex items-center gap-1 px-2 py-1.5"
                >
                  {diagnosingId === scanner.id ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
                  Diagnóstico
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDuplicate(scanner) }}
                  disabled={creating}
                  className="btn-ghost text-xs flex items-center gap-1 px-2 py-1.5"
                >
                  <Copy size={12} />
                  Duplicar
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(scanner.id) }}
                  disabled={deletingId === scanner.id}
                  className="btn-ghost text-xs flex items-center gap-1 px-2 py-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  {deletingId === scanner.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
